import { useState, useCallback } from 'react';
import { Plus, Loader2, CheckCircle, Trash2, Edit } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal, ConfirmDialog, InputDecimal } from '../ui';
import { useApiEffect } from '../../hooks';
import type { PendenciaVeiculo, PendenciaVeiculoCreate, PendenciaVeiculoResumo, TipoPendencia, PrioridadePendencia } from '../../types';
import { formatCurrency, formatDate } from '../../utils/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  veiculoId: number;
  veiculoNome: string;
}

const TIPOS: { value: TipoPendencia; label: string }[] = [
  { value: 'MECANICA', label: 'Mecânica' },
  { value: 'ELETRICA', label: 'Elétrica' },
  { value: 'FUNILARIA', label: 'Funilaria' },
  { value: 'PINTURA', label: 'Pintura' },
  { value: 'DOCUMENTACAO', label: 'Documentação' },
  { value: 'LIMPEZA', label: 'Limpeza' },
  { value: 'OUTROS', label: 'Outros' },
];

const PRIORIDADES: { value: PrioridadePendencia; label: string; color: string }[] = [
  { value: 'BAIXA', label: 'Baixa', color: 'bg-gray-100 text-gray-700' },
  { value: 'MEDIA', label: 'Média', color: 'bg-blue-100 text-blue-700' },
  { value: 'ALTA', label: 'Alta', color: 'bg-orange-100 text-orange-700' },
  { value: 'URGENTE', label: 'Urgente', color: 'bg-red-100 text-red-700' },
];

const statusColors = {
  PENDENTE: 'bg-yellow-100 text-yellow-700',
  EM_ANDAMENTO: 'bg-blue-100 text-blue-700',
  CONCLUIDA: 'bg-green-100 text-green-700',
  CANCELADA: 'bg-gray-100 text-gray-500',
};

const statusLabels = {
  PENDENTE: 'Pendente',
  EM_ANDAMENTO: 'Em Andamento',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

const emptyForm: PendenciaVeiculoCreate = {
  tipo: 'MECANICA',
  descricao: '',
  prioridade: 'MEDIA',
  responsavel: '',
  fornecedor: '',
  custoEstimado: undefined,
  dataPrevista: '',
  observacoes: '',
};

export function PendenciasVeiculoModal({ isOpen, onClose, veiculoId, veiculoNome }: Props) {
  const { showSuccess, showError, showApiError } = useNotification();

  const [pendencias, setPendencias] = useState<PendenciaVeiculo[]>([]);
  const [resumo, setResumo] = useState<PendenciaVeiculoResumo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<PendenciaVeiculoCreate>(emptyForm);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showConcluirDialog, setShowConcluirDialog] = useState(false);
  const [concluindoId, setConcluindoId] = useState<number | null>(null);
  const [custoReal, setCustoReal] = useState<number | null>(null);

  const loadData = useCallback(async (signal: AbortSignal) => {
    if (!veiculoId) return;
    setIsLoading(true);

    try {
      const [pendenciasRes, resumoRes] = await Promise.all([
        api.get<PendenciaVeiculo[]>(`/veiculos/${veiculoId}/pendencias`, { signal }),
        api.get<PendenciaVeiculoResumo>(`/veiculos/${veiculoId}/pendencias/resumo`, { signal }),
      ]);
      setPendencias(pendenciasRes.data);
      setResumo(resumoRes.data);
    } finally {
      setIsLoading(false);
    }
  }, [veiculoId]);

  const { refetch } = useApiEffect(loadData, [veiculoId], { enabled: isOpen && !!veiculoId });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.descricao.trim()) {
      showError('Descrição é obrigatória');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/veiculos/${veiculoId}/pendencias/${editingId}`, form);
        showSuccess('Pendência atualizada');
      } else {
        await api.post(`/veiculos/${veiculoId}/pendencias`, form);
        showSuccess('Pendência criada');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (pendencia: PendenciaVeiculo) => {
    setEditingId(pendencia.id);
    setForm({
      tipo: pendencia.tipo,
      descricao: pendencia.descricao,
      prioridade: pendencia.prioridade,
      responsavel: pendencia.responsavel || '',
      fornecedor: pendencia.fornecedor || '',
      custoEstimado: pendencia.custoEstimado,
      dataPrevista: pendencia.dataPrevista?.split('T')[0] || '',
      observacoes: pendencia.observacoes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/veiculos/${veiculoId}/pendencias/${deletingId}`);
      showSuccess('Pendência excluída');
      setShowDeleteDialog(false);
      setDeletingId(null);
      refetch();
    } catch (error) {
      showApiError(error);
    }
  };

  const handleConcluir = async () => {
    if (!concluindoId) return;
    try {
      await api.post(`/veiculos/${veiculoId}/pendencias/${concluindoId}/concluir`, { custoReal: custoReal ?? undefined });
      showSuccess('Pendência concluída');
      setShowConcluirDialog(false);
      setConcluindoId(null);
      setCustoReal(null);
      refetch();
    } catch (error) {
      showApiError(error);
    }
  };

  const openConcluirDialog = (id: number, custoEstimado?: number) => {
    setConcluindoId(id);
    setCustoReal(custoEstimado ?? null);
    setShowConcluirDialog(true);
  };

  const getPrioridadeStyle = (prioridade: PrioridadePendencia) => {
    return PRIORIDADES.find((p) => p.value === prioridade)?.color || 'bg-gray-100 text-gray-700';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Pendências - ${veiculoNome}`} size="xl">
      <div className="space-y-4">
        {/* Resumo */}
        {resumo && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-lg font-semibold">{resumo.totalPendencias}</p>
            </div>
            <div>
              <p className="text-sm text-yellow-600">Pendentes</p>
              <p className="text-lg font-semibold text-yellow-600">{resumo.pendentes}</p>
            </div>
            <div>
              <p className="text-sm text-blue-600">Em Andamento</p>
              <p className="text-lg font-semibold text-blue-600">{resumo.emAndamento}</p>
            </div>
            <div>
              <p className="text-sm text-green-600">Concluídas</p>
              <p className="text-lg font-semibold text-green-600">{resumo.concluidas}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {resumo?.custoEstimadoTotal
              ? `Custo estimado total: ${formatCurrency(resumo.custoEstimadoTotal)}`
              : ''}
          </p>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setForm(emptyForm);
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus className="w-4 h-4" />
            Nova Pendência
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value as TipoPendencia })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {TIPOS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
                <select
                  value={form.prioridade}
                  onChange={(e) => setForm({ ...form, prioridade: e.target.value as PrioridadePendencia })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {PRIORIDADES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
              <input
                type="text"
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Descreva a pendência..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Responsável</label>
                <input
                  type="text"
                  value={form.responsavel}
                  onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
                <input
                  type="text"
                  value={form.fornecedor}
                  onChange={(e) => setForm({ ...form, fornecedor: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custo Estimado</label>
                <InputDecimal
                  value={form.custoEstimado}
                  onChange={(value) => setForm({ ...form, custoEstimado: value ?? undefined })}
                  prefix="R$ "
                  placeholder="0,00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Prevista</label>
                <input
                  type="date"
                  value={form.dataPrevista}
                  onChange={(e) => setForm({ ...form, dataPrevista: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
              <textarea
                value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingId ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        )}

        {/* List */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : pendencias.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Nenhuma pendência cadastrada</p>
          ) : (
            pendencias.map((pendencia) => (
              <div
                key={pendencia.id}
                className={`p-4 border rounded-lg ${
                  pendencia.status === 'CONCLUIDA' ? 'bg-green-50 border-green-200' : 'bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs rounded font-medium ${getPrioridadeStyle(pendencia.prioridade)}`}>
                        {PRIORIDADES.find((p) => p.value === pendencia.prioridade)?.label}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded font-medium ${statusColors[pendencia.status]}`}>
                        {statusLabels[pendencia.status]}
                      </span>
                      <span className="text-xs text-gray-500">
                        {TIPOS.find((t) => t.value === pendencia.tipo)?.label}
                      </span>
                    </div>
                    <p className="font-medium text-gray-900">{pendencia.descricao}</p>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                      {pendencia.responsavel && <span>Resp: {pendencia.responsavel}</span>}
                      {pendencia.fornecedor && <span>Forn: {pendencia.fornecedor}</span>}
                      {pendencia.custoEstimado && <span>Est: {formatCurrency(pendencia.custoEstimado)}</span>}
                      {pendencia.custoReal && <span className="text-green-600">Real: {formatCurrency(pendencia.custoReal)}</span>}
                      {pendencia.dataPrevista && <span>Prev: {formatDate(pendencia.dataPrevista)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {pendencia.status !== 'CONCLUIDA' && pendencia.status !== 'CANCELADA' && (
                      <button
                        onClick={() => openConcluirDialog(pendencia.id, pendencia.custoEstimado)}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                        title="Concluir"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(pendencia)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setDeletingId(pendencia.id);
                        setShowDeleteDialog(true);
                      }}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        type="danger"
        title="Excluir Pendência"
        message="Tem certeza que deseja excluir esta pendência?"
        confirmText="Excluir"
      />

      <Modal
        isOpen={showConcluirDialog}
        onClose={() => setShowConcluirDialog(false)}
        title="Concluir Pendência"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">Informe o custo real da pendência (opcional):</p>
          <InputDecimal
            value={custoReal}
            onChange={setCustoReal}
            prefix="R$ "
            placeholder="0,00"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowConcluirDialog(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConcluir}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4" />
              Concluir
            </button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
}
